import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { resolveCoverUrl } from '../../services/api';
import BookCard from '../../components/BookCard';
import { LoaderIcon, SearchIcon, MoonIcon, SunIcon } from '../../components/Icons';
import Navigation from '../../components/Navigation';
import styles from './Home.module.css';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const SLIDE_INTERVAL_MS = 5000;

const Home = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  // 临时隐藏轮播图
  const showCarousel = false;
  const [carouselItems, setCarouselItems] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categorySections, setCategorySections] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState('');
  const [latestNovels, setLatestNovels] = useState([]);
  const [recommendedNovels, setRecommendedNovels] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(true);
  const [highlightsError, setHighlightsError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchCarouselItems = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await api.get('/carousel-items/', {
          params: { active_only: true },
        });
        if (Array.isArray(data)) {
          setCarouselItems(data);
          setCurrentSlide(0);
        } else {
          setCarouselItems([]);
          console.warn('Unexpected carousel payload:', data);
        }
      } catch (err) {
        console.error('获取轮播图失败:', err);
        setError('轮播图加载失败，请稍后重试');
        setCarouselItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCarouselItems();
    fetchFeaturedCategories();
    fetchHighlights();
  }, []);

  const fetchFeaturedCategories = async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError('');
      const { data } = await api.get('/categories/', {
        params: {
          limit: 50,
        },
      });
      if (Array.isArray(data)) {
        const normalized = data
          .map((category) => {
            const sortedNovels = [...(category.novels || [])].sort((a, b) => {
              const orderA =
                typeof a.feature_order === 'number' ? a.feature_order : Number.MAX_SAFE_INTEGER;
              const orderB =
                typeof b.feature_order === 'number' ? b.feature_order : Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              return (a.id || 0) - (b.id || 0);
            });

            const topThree = sortedNovels.slice(0, 3);
            return {
              id: category.id,
              name: category.name,
              feature_order: category.feature_order ?? 0,
              novels: topThree,
            };
          })
          .filter((section) => section.novels && section.novels.length > 0)
          .sort((a, b) => a.feature_order - b.feature_order);

        setCategorySections(normalized);
      } else {
        setCategorySections([]);
      }
    } catch (err) {
      console.error('获取分类数据失败:', err);
      setCategoriesError('分类数据加载失败，请稍后重试');
      setCategorySections([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchHighlights = async () => {
    try {
      setHighlightsLoading(true);
      setHighlightsError('');
      const [latestRes, recommendedRes] = await Promise.all([
        api.get('/novels/latest', {
          params: { limit: 6 },
        }),
        api.get('/novels/recommended', {
          params: { limit: 6 },
        }),
      ]);

      setLatestNovels(Array.isArray(latestRes.data) ? latestRes.data : []);
      setRecommendedNovels(Array.isArray(recommendedRes.data) ? recommendedRes.data : []);
    } catch (err) {
      console.error('获取推荐小说失败:', err);
      setHighlightsError('推荐数据加载失败，请稍后重试');
      setLatestNovels([]);
      setRecommendedNovels([]);
    } finally {
      setHighlightsLoading(false);
    }
  };

  useEffect(() => {
    if (carouselItems.length <= 1) {
      return undefined;
    }

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, SLIDE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [carouselItems.length]);

  useEffect(() => {
    if (currentSlide >= carouselItems.length) {
      setCurrentSlide(0);
    }
  }, [carouselItems.length, currentSlide]);

  const hasCarousel = useMemo(() => carouselItems.length > 0, [carouselItems.length]);

  const handleIndicatorClick = (index) => {
    setCurrentSlide(index);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const normalized = searchQuery.trim();
    const params = new URLSearchParams();
    if (normalized) {
      params.set('search', normalized);
    }
    navigate(`/discover${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const renderSlideContent = (item, index) => {
    const linkUrl =
      typeof item.link_url === 'string' ? item.link_url.trim() : '';
    const SlideComponent = linkUrl ? 'a' : 'div';
    const slideProps = linkUrl
      ? {
          href: linkUrl,
          target: linkUrl.startsWith('http') ? '_blank' : undefined,
          rel: linkUrl.startsWith('http') ? 'noopener noreferrer' : undefined,
        }
      : {};

    return (
      <SlideComponent
        key={item.id || index}
        className={styles.carouselSlide}
        {...slideProps}
      >
        <img
          src={item.image_url}
          alt={item.title || `轮播图 ${index + 1}`}
          className={styles.carouselImage}
          loading={index === 0 ? 'eager' : 'lazy'}
        />
        {(item.title || item.link_url) && (
          <div className={styles.carouselCaption}>
            <span>{item.title || '查看详情'}</span>
          </div>
        )}
      </SlideComponent>
    );
  };

  const handleViewMore = (category) => {
    if (!category) {
      navigate('/discover');
      return;
    }

    const { id, name } = category;
    if (id) {
      navigate(`/categories/${id}`, {
        state: {
          categoryName: name || '',
        },
      });
      return;
    }

    if (name) {
      navigate(`/discover?category=${encodeURIComponent(name)}`);
      return;
    }

    navigate('/discover');
  };

  return (
    <div className={styles.home}>
      {/* 移动端顶部栏 - 仅在移动端显示 */}
      <div className={styles.mobileTopBar}>
        <div className={styles.mobileTopBarLeft}>
          <div className={styles.mobileTopBarBrand}>
            <div className={styles.mobileTopBarBrandIcon} aria-hidden="true">
              <span className="material-icons-round" style={{ color: '#fff' }}>
                auto_stories
              </span>
            </div>
            <span className={styles.mobileTopBarBrandLabel}>EnNovels</span>
          </div>
        </div>
        <div className={styles.mobileTopBarRight}>
          <button
            type="button"
            className={styles.mobileTopBarButton}
            onClick={toggleTheme}
            aria-label={isDark ? '切换到浅色模式' : '切换到夜晚模式'}
          >
            <span className={styles.mobileTopBarButtonIcon}>
              {isDark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
            </span>
          </button>
          <button
            type="button"
            className={styles.mobileTopBarButton}
            onClick={logout}
            aria-label="退出登录"
          >
            <span className={styles.mobileTopBarButtonText}>退出</span>
          </button>
        </div>
      </div>

      <header className={styles.heroHeader}>
        {/* <div className={styles.heroText}>
          <h1 className={styles.heroTitle}>欢迎回来</h1>
          <p className={styles.heroSubtitle}>发现您感兴趣的下一个故事</p>
        </div> */}
        <div className={styles.heroActions}>
          <form className={styles.heroSearch} onSubmit={handleSearchSubmit}>
            <span className={styles.heroSearchIcon}>
              <SearchIcon size={18} />
            </span>
            <input
              className={styles.heroSearchInput}
              placeholder="搜索书名、作者..."
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </form>
        </div>
      </header>

      {/* 轮播区域暂时隐藏，保持界面清爽
      <section className={styles.heroCarousel}>
        <div className={styles.heroCarouselGrid}>
          {[1, 2].map((idx) => (
            <div key={`hero-card-${idx}`} className={styles.heroCarouselMedia}>
              <img
                alt="Featured Novel"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuBDrtXmDVKonmAm_hhIuNS0YonT1dVuc385-MOvBS61cO8-WciJ9PwYoQIGIlk7SJddaLOaH6Oy1IUrSAGh76pFVOOGnD9g7XnXY2IP5_9dTG6Y0bX1u86fCvCDZq7uqqjuVLaqvU3iIHBDxEhiWdFE3noEjhgJehPD4dH5LkHhog_0F_oEokY5gfREs_sW-mePqMgvmWMKI4hxk6c-OdJkA9AM9rmTZ9fLKFX8pM62C8ySmykN0c3iZixsoLznSzqwe1TumLvrpg"
              />
              <div className={styles.heroCarouselOverlay}>
                <div className={styles.heroCarouselContent}>
                  <span className={styles.heroBadge}>本周主打推荐</span>
                  <h2 className={styles.heroHeadline}>剑破苍穹：逆天归来</h2>
                  <p className={styles.heroDescription}>
                    在这片广袤的修真大陆上，少年带着前世的记忆，凭借一把锈迹斑斑的长剑，
                    开启了挑战诸神、重塑轮回的逆天之路。真相背后，究竟隐藏着怎样的万古棋局？
                  </p>
                  <div className={styles.heroButtons}>
                    <button type="button" className={styles.heroPrimaryButton}>
                      立即阅读
                    </button>
                    <button type="button" className={styles.heroGhostButton}>
                      加入书架
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      */}

      {showCarousel && (
      <section className={styles.carouselSection}>
        {loading && (
          <div className={styles.carouselStatus} role="status">
            正在加载轮播图...
          </div>
        )}

        {!loading && error && (
          <div className={styles.carouselStatus} role="alert">
            {error}
          </div>
        )}

        {!loading && !error && hasCarousel && (
          <div className={styles.carousel}>
            <div
              className={styles.carouselTrack}
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {carouselItems.map((item, index) => renderSlideContent(item, index))}
            </div>

            {carouselItems.length > 1 && (
              <div className={styles.carouselIndicators}>
                {carouselItems.map((_, index) => (
                  <button
                    key={`indicator-${index}`}
                    type="button"
                    className={`${styles.carouselIndicator} ${
                      currentSlide === index ? styles.carouselIndicatorActive : ''
                    }`}
                    onClick={() => handleIndicatorClick(index)}
                    aria-label={`切换到第 ${index + 1} 张`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      )}

      <section className={styles.categoryShowcase}>
        {categoriesLoading && (
          <div className={styles.categoryStatus} role="status">
            正在加载分类...
          </div>
        )}

        {!categoriesLoading && categoriesError && (
          <div className={styles.categoryStatus} role="alert">
            {categoriesError}
          </div>
        )}

        {!highlightsLoading && highlightsError && (
          <div className={styles.categoryStatus} role="alert">
            {highlightsError}
          </div>
        )}

        {!highlightsLoading && !highlightsError && (
          <>
            <div className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <h2 className={styles.categoryTitle}>最近更新</h2>
                <button
                  type="button"
                  className={styles.categoryMore}
                  onClick={() => navigate('/discover')}
                >
                  更多
                </button>
              </div>
              <div className={styles.categoryBooks}>
                {latestNovels.length > 0 ? (
                  latestNovels.map((novel) => (
                    <BookCard
                      key={`latest-${novel.id}`}
                      book={{
                        id: novel.id,
                        title: novel.title,
                        author: novel.author,
                        description: novel.description,
                        cover_image_url: resolveCoverUrl(novel.cover_image_url || novel.cover_img_url),
                        category: Array.isArray(novel.categories) && novel.categories[0]?.name,
                        is_completed: novel.is_completed ?? false,
                      }}
                      from="/"
                      showStatus
                    />
                  ))
                ) : (
                  <div className={styles.categoryStatus}>暂无最近更新的作品</div>
                )}
              </div>
            </div>

            <div className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <h2 className={styles.categoryTitle}>精选推荐</h2>
                <button
                  type="button"
                  className={styles.categoryMore}
                  onClick={() => navigate('/discover')}
                >
                  更多
                </button>
              </div>
              <div className={styles.categoryBooks}>
                {recommendedNovels.length > 0 ? (
                  recommendedNovels.map((novel) => (
                    <BookCard
                      key={`rec-${novel.id}`}
                      book={{
                        id: novel.id,
                        title: novel.title,
                        author: novel.author,
                        description: novel.description,
                        cover_image_url: resolveCoverUrl(novel.cover_image_url || novel.cover_img_url),
                        category: Array.isArray(novel.categories) && novel.categories[0]?.name,
                        is_completed: novel.is_completed ?? false,
                      }}
                      from="/"
                      showStatus
                    />
                  ))
                ) : (
                  <div className={styles.categoryStatus}>暂无推荐的作品</div>
                )}
              </div>
            </div>
          </>
        )}

        {!categoriesLoading && !categoriesError && categorySections.map((section) => (
          <div key={section.id} className={styles.categorySection}>
            <div className={styles.categoryHeader}>
              <h2 className={styles.categoryTitle}>{section.name}</h2>
              <button
                type="button"
                className={styles.categoryMore}
                onClick={() => handleViewMore(section)}
              >
                更多
              </button>
            </div>
            <div className={styles.categoryBooks}>
              {section.novels.map((novel) => {
                const bookData = {
                  id: novel.id,
                  title: novel.title,
                  author: novel.author,
                  description: novel.description,
                  cover_image_url: resolveCoverUrl(novel.cover_image_url || novel.cover_img_url),
                  category: section.name,
                  is_completed: novel.is_completed ?? false,
                };
                return (
                  <BookCard
                    key={`${section.id}-${novel.id}`}
                    book={bookData}
                    from="/"
                    showStatus
                  />
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};

export default Home;
